import { Box, Typography } from "@mui/material";

import AttachmentDrawer from "../ChatAttachments/AttachmentDrawer";
import { ChunkedFileWithPreview } from "../types/chunkedUploadTypes";
import { FileUploadState } from "../../types/fileUploadWithProgress";
import { formatFileSize } from "src/helpers/formatFileSize";
import { useContext } from "react";
import ChatContext from "../context/ChatContext";
import { useGetDialoguesById } from "src/views/assistant/AssistantServices";

interface AttachmentsContainerProps {
  processedFiles: FileUploadState[];
  removeFile: (fileId: string) => void; // Changed from index to fileId
  isDragActive: boolean;
  uploadError: string | null;
  /** Dropzone rejection; shown whether or not files are already attached */
  validationError?: string | null;
  pendingArtifactFileIds?: Map<string, "finalizing" | "failed">;
}

const AttachmentsContainer = ({
  processedFiles,
  removeFile,
  isDragActive,
  uploadError,
  validationError,
  pendingArtifactFileIds,
}: AttachmentsContainerProps) => {
  // A project chat is a dialogue bound to a project folder. Resolved once here
  // (not per attachment row) and threaded down to gate the ad-hoc upload
  // "Add to Project" surface. AttachmentsContainer is also reused outside a
  // ChatContext (proposal-generation, vnv-assist, project file upload), so read
  // the context optionally rather than via useChatContext(), which throws.
  const dialogueId = useContext(ChatContext)?.dialogueId;
  const { data: dialogue } = useGetDialoguesById(dialogueId);
  const isProjectChat = dialogue?.project_folder_id != null;

  // Convert FileUploadState to ChunkedFileWithPreview format
  const convertedFiles: ChunkedFileWithPreview[] = processedFiles.map(
    (fileState) => {
      // Check if this is a project file (not deletable from dialogue)
      const isProjectFile =
        (fileState as any).isFromProject === true ||
        fileState.id.startsWith("project-") ||
        fileState.id.startsWith("proj-attachment-");

      // Create a new file-like object with the required properties.
      // Preserve the original sizeFormatted when present, otherwise format
      // from the raw byte count (if any).
      const preservedSize =
        fileState.sizeFormatted ||
        (fileState.file.size > 0
          ? formatFileSize(fileState.file.size)
          : undefined);

      const convertedFile = Object.assign(fileState.file, {
        id: fileState.id,
        preview: fileState.preview,
        sizeFormatted: preservedSize,
        isProjectFile, // Add project file flag
        // The row and the drawer read these to show a failed upload.
        uploadStatus: fileState.uploadStatus,
        error: fileState.error,
        isUploading:
          fileState.uploadStatus === "uploading" ||
          (fileState.uploadStatus === "uploaded" &&
            fileState.processingStatus === "processing" &&
            fileState.processingProgress < 100) || // Only show if processing and not at 100%
          (fileState.syncStatus === "pending-backend-sync" &&
            fileState.processingStatus !== "completed"), // Don't show sync overlay if processing is complete
        uploadProgress:
          fileState.uploadStatus === "uploading"
            ? fileState.uploadProgress
            : fileState.uploadStatus === "uploaded" &&
                fileState.processingStatus !== "completed"
              ? 100 // Show 100% for uploaded files
              : fileState.processingStatus === "processing"
                ? 100 // Show 100% during processing (embeddingProgress will show actual progress)
                : fileState.syncStatus === "pending-backend-sync"
                  ? 100
                  : 100,
        totalChunks: fileState.totalChunks,
        uploadedChunks: fileState.uploadedChunks,
        num_sdr: fileState.numSdr,
        num_sdr_not_embedded: fileState.numSdrNotEmbedded,
        embeddingProgress: fileState.processingProgress,
        uniqueId: fileState.id,
        uploadId: fileState.uploadId,
        datasetId: fileState.datasetId, // Pass through for AttachmentListItem
        sourceDataRecordId:
          fileState.sourceDataRecordId ||
          (fileState.file as any).sourceDataRecordId, // Pass SDR ID
        workspacePath: fileState.workspacePath, // Pass through for workspace upload detection
        origin: fileState.origin, // Authoritative source classifier (set at upload)
        // Ready if uploaded and completed — workspace uploads have no datasetId but are still ready
        isReady:
          fileState.uploadStatus === "uploaded" &&
          fileState.processingStatus === "completed" &&
          (!!fileState.datasetId || !!fileState.workspacePath),
        processingProgress: fileState.processingProgress,
      }) as ChunkedFileWithPreview & {
        isProjectFile?: boolean;
        sourceDataRecordId?: number;
      };

      return convertedFile;
    },
  );

  return (
    <>
      {isDragActive && (
        <Box
          data-testid="aa-attachments-drop-zone"
          data-attr="assistant:input:file-drop-zone"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderRadius: "4px",
            pointerEvents: "none", // Prevent this overlay from interfering with drag events
          }}
        >
          <Typography variant="body1" color="secondary">
            Drop files here...
          </Typography>
        </Box>
      )}
      {processedFiles.length > 0 && (
        <Box
          data-testid="aa-attachments-container"
          sx={{
            p: 1,
            position: "relative", // Ensure proper positioning context
          }}
        >
          <AttachmentDrawer
            files={convertedFiles}
            onRemove={removeFile}
            isEditable={true}
            pendingArtifactFileIds={pendingArtifactFileIds}
            isProjectChat={isProjectChat}
          />
        </Box>
      )}
      {validationError && (
        <Box sx={{ p: 1 }}>
          <Typography
            data-testid="aa-attachments-validation-error"
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 1 }}
          >
            {validationError}
          </Typography>
        </Box>
      )}
      {processedFiles.length === 0 && uploadError && (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 1 }}
          >
            {uploadError}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default AttachmentsContainer;
